import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api'
import { noteText } from './notes'
import { CreditsOffer, DiscardsOffer } from './Offers.jsx'
import Button from './Button.jsx'
import PostModal from './PostModal.jsx'
import SelectionBar from './SelectionBar.jsx'

export default function Posts({
  canManage,
  business,
  timezoneId,
  refreshToken,
  onScheduled,
  onAskDesigner,
}) {
  const { t } = useTranslation()
  // Approved first: drafts are what the assistant just made and the owner has usually seen; what
  // brings someone back to this tab is the pile that is ready and waiting to go out.
  const [status, setStatus] = useState('Approved')
  const [posts, setPosts] = useState([])
  const [count, setCount] = useState(3)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  // Which post is open. The list decides what to look at; the sheet is for looking at it.
  const [open, setOpen] = useState(null)

  // The same picking as the calendar, for the same reason: a campaign is thirty posts and
  // approving them one at a time is thirty round trips through a sheet.
  const [selected, setSelected] = useState(null)

  // A price the platform is asking for, named by the server. The same offers the conversation
  // shows, for the same refusals — the button that says "no room" in one screen and offers a way
  // on in the other was two products in one app.
  const [offer, setOffer] = useState(null)

  const refresh = useCallback(async () => {
    try {
      setPosts(await api.posts(status))
    } catch (failure) {
      setError(failure.message)
    }
  }, [status])

  // Reloaded when the assistant reports it has written something, so the queue is already full by
  // the time anyone switches to this tab.
  useEffect(() => {
    refresh()
  }, [refresh, refreshToken])

  // A draft written before the renderer existed has no picture and no reason to make the owner ask
  // for one: building it costs a download and a render, no model call. Tried once per post, so a
  // product that genuinely has no usable photograph does not turn into a loop.
  const attempted = useRef(new Set())

  useEffect(() => {
    const missing = posts.filter(
      (post) => !attempted.current.has(post.id) && post.variants.some((variant) => !variant.imageUrl),
    )

    if (missing.length === 0) return

    missing.forEach((post) => attempted.current.add(post.id))

    Promise.allSettled(missing.map((post) => api.buildImage(post.id))).then(refresh)
  }, [posts, refresh])

  async function run(action) {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await action()
    } catch (failure) {
      setError(failure.message)
    } finally {
      setBusy(false)
    }
  }

  const suggest = () =>
    run(async () => {
      const result = await api.suggestPosts(Number(count), 'Rotation')

      // Green is for something that happened. Nothing written is not a success with a number in
      // it — the reason is what matters, and it comes back as a note the portal words.
      if (result.generated > 0) {
        setNotice(t('posts.suggested', { generated: result.generated, requested: result.requested }))
      }

      setOffer(result.offer ?? null)

      if (result.failures?.length) {
        const said = result.failures.map((note) => noteText(t, note)).filter(Boolean).join(' · ')

        // With an offer under it the reason is the offer's opening line, not a red warning.
        if (result.offer) setNotice(said)
        else setError(said)
      }
      await refresh()
    })


  const selecting = selected !== null
  const allIds = posts.map((post) => post.id)

  function toggle(postId) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(postId)) next.delete(postId)
      else next.add(postId)
      return next
    })
  }

  async function bulk(action) {
    await run(async () => {
      await api.bulkPosts(action, [...selected])
      setSelected(null)
      await refresh()
      onScheduled?.()
    })
  }

  return (
    <section className="card">
      <div className="row">
        <h2>{t('posts.title')}</h2>
        <div className="row row--form">
          {canManage && posts.length > 0 && (
            <button
              type="button"
              className="button button--quiet"
              onClick={() => setSelected(selecting ? null : new Set())}
            >
              {selecting ? t('common.cancel') : t('calendar.select')}
            </button>
          )}

          <select value={status} disabled={busy} onChange={(event) => setStatus(event.target.value)}>
            <option value="Approved">{t('posts.status.Approved')}</option>
            <option value="Draft">{t('posts.status.Draft')}</option>
            <option value="Scheduled">{t('posts.status.Scheduled')}</option>
            <option value="Published">{t('posts.status.Published')}</option>
            <option value="Cancelled">{t('posts.status.Cancelled')}</option>
          </select>

          {canManage && (
            <>
              <input
                type="number"
                min="1"
                max="10"
                value={count}
                disabled={busy}
                onChange={(event) => setCount(event.target.value)}
                className="count"
              />
              <Button type="button" className="button" loading={busy} onClick={suggest}>
                {t('posts.suggest')}
              </Button>
            </>
          )}
        </div>
      </div>

      {notice && <p className={`status${offer ? '' : ' status--good'}`}>{notice}</p>}
      {error && <p className="status status--bad">{error}</p>}

      {/* The same three the conversation offers, for the same three refusals. */}
      {offer === 'discards' && (
        <DiscardsOffer
          busy={busy}
          onAccept={() =>
            run(async () => {
              await api.buyDiscards()
              setOffer(null)
              setNotice(null)
              onScheduled?.()
            })
          }
          onDecline={() => setOffer(null)}
        />
      )}

      {(offer === 'credits' || offer === 'room') && (
        <CreditsOffer busy={busy} onAsked={onScheduled} lead="" ways={offer === 'room'} />
      )}
      {posts.length === 0 && <p className="muted">{t('posts.empty')}</p>}

      {posts.map((post) => {
        const variant = post.variants[0]

        return (
          <button
            key={post.id}
            type="button"
            className={`postrow${selecting ? ' postrow--picking' : ''}${
              selecting && selected.has(post.id) ? ' postrow--picked' : ''
            }`}
            onClick={() => (selecting ? toggle(post.id) : setOpen(post.id))}
          >
            {selecting && (
              <input
                type="checkbox"
                className="postrow__pick"
                checked={selected.has(post.id)}
                readOnly
                tabIndex={-1}
                aria-hidden="true"
              />
            )}

            {variant?.imageUrl ? (
              <img className="postrow__thumb" src={variant.imageUrl} alt="" loading="lazy" />
            ) : (
              <span className="postrow__thumb postrow__thumb--empty">🖼</span>
            )}

            <span className="postrow__body">
              <span className="postrow__title">{post.products?.[0]?.name ?? `#${post.id}`}</span>
              <span className="postrow__text">{variant?.text}</span>
            </span>

            <span className="postrow__meta">
              {post.scheduledAt && (
                <span className="muted small">{new Date(post.scheduledAt).toLocaleDateString()}</span>
              )}
              {/* The chevron says "this opens". While picking, it does not. */}
              {!selecting && <span aria-hidden="true">›</span>}
            </span>
          </button>
        )
      })}

      {selecting && (
        <SelectionBar
          selected={selected}
          allIds={allIds}
          busy={busy}
          onSelect={setSelected}
          onApprove={() => bulk('approve')}
          onRemove={() => bulk('reject')}
        />
      )}

      <PostModal
        post={posts.find((post) => post.id === open) ?? null}
        business={business}
        timezoneId={timezoneId}
        canManage={canManage}
        busy={busy}
        onClose={() => setOpen(null)}
        onAskDesigner={onAskDesigner}
        onChanged={() => {
          run(refresh)
          onScheduled?.()
        }}
        onReplaced={(id) => {
          // The rewrite is a draft. Showing the approved list and an open sheet on a post that is
          // not in it is how the sheet used to close on someone who had just asked for a rewrite.
          setStatus('Draft')
          setOpen(id)
        }}
      />

    </section>
  )
}
